import { pushRoute } from '../../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { setLicenseKey } from 'survey-core';
import Empty from '../../../components/Empty.jsx';
import { loadingContent } from '../../../components/EmptyStates.jsx';
import SubHeader from '../../../components/SubHeader.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import {
  createChangeInsight,
  deleteChangeInsightTemplate,
  retrieveTemplatesFeedbackForm,
  selectTemplates,
  updateChangeInsight,
} from '../../../modules/changeInsights.js';
import {
  retrieveOrganization,
  selectOrganization,
} from '../../../modules/organizations.js';
import { retrieveProject } from '../../../modules/projects.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import {
  selectCurrentOrganizationId,
  selectUser as selectCurrentUser,
} from '../../../modules/auth.js';
import { selectUser } from '../../../modules/users.js';
import {
  CUSTOM_DEFAULT_BRAND_COLOR,
  CUSTOM_DEFAULT_FONT,
} from '../../change-insights/utils/index.jsx';
import OrgSettingsHeader from '../../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import OrgSurveySettingForm from '../../../components/organizations/Settings/OrgSurveySettingForm.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  userId: ownProps.userId,
  organizationId: selectCurrentOrganizationId(state),
  organization: selectOrganization(state),
  currentUser: selectCurrentUser(state),
  user: selectUser(ownProps.userId)(state),
  changeInsights: selectTemplates(state),
  surveyjsKey: state.config.surveyjsKey,
});

const mapDispatchToProps = {
  retrieveProject,
  createChangeInsight,
  deleteChangeInsightTemplate,
  retrieveTemplatesFeedbackForm,
  updateChangeInsight,
  retrieveOrganization,
};

const validate = (values) => {
  const errors = {};
  if (!values.status) {
    errors.name = 'Status is required';
  }
  return errors;
};

const OrgSurveySettingsFormContainer = (props) => {
  const {
    slug,
    organization,
    changeInsights,
    createChangeInsight,
    retrieveTemplatesFeedbackForm,
    deleteChangeInsightTemplate,
    updateChangeInsight,
    retrieveOrganization,
    organizationId,
    templateId,
    edit,
    surveyjsKey,
  } = props;
  const [data, setData] = useState(null);
  const [errorsFields, setErrorsFields] = useState(null);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [isFirstLoading, setIsLoadingFirstLoading] = useState(true);
  setLicenseKey(surveyjsKey);

  const navigateToSettings = () => pushRoute('organization/custom-settings');
  const navigateToTemplate = (id) =>
    pushRoute(
      `/${slug}/organization/custom-settings/change-insights/template/${id}`
    ).then(() => {
      window.scrollTo(0, 0);
    });

  const handleClickDeleteSurveyTemplate = () => {
    if (
      window.confirm(
        `${getOrgTermsContent('deleteFeedbackFormTemplate')} \n\n${getOrgTermsContent('deleteFeedbackFormTemplateDescription')}`
      )
    ) {
      deleteChangeInsightTemplate(data.id).then(() => {
        navigateToSettings();
      });
    }
  };

  const onSubmit = (template) => {
    if (status && template.pages && template?.title) {
      setErrorsFields(null);
      const payload = {
        title: template.title,
        status,
        description: template.description,
        surveyJson: JSON.stringify(template),
      };

      setLoading(true);

      const actionPromise = edit
        ? updateChangeInsight(data.id, payload)
        : createChangeInsight(payload);

      actionPromise
        .then((res) => {
          navigateToTemplate(edit ? data.id : res.result);
          retrieveTemplatesFeedbackForm();
        })
        .catch((e) => {
          setErrorsFields({ field: '', message: e.message });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      if (!status) {
        setErrorsFields({ field: 'status', message: 'Status is required.' });
      }
      if (!template.title) {
        setErrorsFields({ field: 'content', message: 'Title is required.' });
      }
      if (!template.pages) {
        setErrorsFields({ field: 'content', message: 'Please fill template.' });
      }
    }
  };

  const handleChangeStatus = (value) => {
    setStatus(value);
    setErrorsFields(null);
  };

  const onPreview = (surveyTemplate) => {
    if (surveyTemplate?.pages && surveyTemplate?.title) {
      localStorage.setItem(
        'draftTemplate',
        JSON.stringify({
          surveyJson: surveyTemplate,
          settings: {
            brandColor: organization.brandColor || CUSTOM_DEFAULT_BRAND_COLOR,
            fontName: organization.fontName || CUSTOM_DEFAULT_FONT,
          },
        })
      );
      window.open(
        `/${slug}/organization/custom-settings/change-insights/preview/${edit ? `edit/${templateId}` : 'new'}`,
        '_blank'
      );
    } else {
      if (!surveyTemplate.title) {
        setErrorsFields({ field: 'content', message: 'Title is required.' });
      }
      if (!surveyTemplate.pages) {
        setErrorsFields({ field: 'content', message: 'Please fill template.' });
      }
    }
  };

  useEffect(() => {
    setIsLoadingFirstLoading(true);
    retrieveOrganization(organizationId);
    retrieveTemplatesFeedbackForm().finally(() => {
      setIsLoadingFirstLoading(false);
    });
  }, []);

  useEffect(() => {
    if (edit && templateId && changeInsights) {
      const editableTemplate = changeInsights.find(
        (ci) => ci.id === templateId
      );
      setData(editableTemplate);
      setStatus(editableTemplate?.status ? editableTemplate.status : null);
    }
  }, [changeInsights]);

  return (
    <React.Fragment>
      <OrgSettingsHeader
        slug={slug}
        page={`${!edit ? 'Add' : 'Edit'} Feedback Form Template`}
      />
      <SubHeader flexJustify>
        <div className="flex w-100" style={{ justifyContent: 'space-between' }}>
          <h2 className="m-r-10 m-l-16">
            {!edit ? 'Add' : 'Edit'} Feedback Form Template
          </h2>
          {edit ? (
            <Button
              className="btn btn-danger"
              onClick={handleClickDeleteSurveyTemplate}
            >
              Delete
            </Button>
          ) : null}
        </div>
      </SubHeader>

      <Grid>
        {isFirstLoading ? (
          <Empty simplified content={loadingContent} />
        ) : (
          <Row>
            <Col sm={12} style={{ background: '#ffffff', padding: '0' }}>
              <Form
                component={OrgSurveySettingForm}
                onSubmit={onSubmit}
                handleSubmit={onSubmit}
                handlePreview={onPreview}
                handleChangeStatus={handleChangeStatus}
                validate={validate}
                data={data}
                loading={loading}
                edit={edit}
                errorsFields={errorsFields}
                status={status}
                slug={slug}
                organization={organization}
              />
            </Col>
          </Row>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgSurveySettingsFormContainer);
